import * as React from "react"

import type { Finding, FindingId, FindingSeverity } from "../data/review-data"

import { rectToStyle, type PageGeometry } from "./pdf-surface"

import { cn } from "@/lib/utils"

/*
   Severity as the palette defines it, never a hue picked here: `--err`,
   `--print` and `--info` are the same marks the rail and the report use, so a
   box on the page and its row in the list read as one finding.

   Opacity rather than a tint token, because these sit over the label artwork
   and have to let the printed text show through.
*/
const BOX_TONE: Record<FindingSeverity, string> = {
  error: "border-[color:var(--err)] bg-[color:var(--err)]/8",
  warning: "border-[color:var(--print)] bg-[color:var(--print)]/10",
  info: "border-[color:var(--info)] bg-[color:var(--info)]/10",
}

const BOX_TONE_ACTIVE: Record<FindingSeverity, string> = {
  error: "border-[color:var(--err)] bg-[color:var(--err)]/20",
  warning: "border-[color:var(--print)] bg-[color:var(--print)]/22",
  info: "border-[color:var(--info)] bg-[color:var(--info)]/22",
}

/** The severity token behind the active box's halo. */
const HALO_TOKEN: Record<FindingSeverity, string> = {
  error: "var(--err)",
  warning: "var(--print)",
  info: "var(--info)",
}

/** How wide the active box's halo is on screen, in CSS pixels, at any zoom. */
const HALO_WIDTH = 4

/**
 * The halo round the active box. Inline rather than a class, so its width is
 * counter-scaled with the border: drawn on a page scaled 4x, a fixed 4px halo
 * grows to 16px and covers the neighbouring text.
 */
function haloStyle(severity: FindingSeverity, zoom: number): string {
  return `0 0 0 ${String(HALO_WIDTH / zoom)}px color-mix(in srgb, ${HALO_TOKEN[severity]} 28%, transparent)`
}

/*
   Graphite while idle, whatever the severity: an unselected badge is a handle,
   and three colours of handle on one page competes with the boxes for the
   reviewer's eye. The active badge takes the severity colour, so exactly one
   mark on the page is coloured at a time.
*/
const BADGE_IDLE = "bg-text text-surface ring-surface/60"

const BADGE_TONE_ACTIVE: Record<FindingSeverity, string> = {
  error: "bg-[color:var(--err)] text-surface ring-surface",
  warning: "bg-[color:var(--print)] text-surface ring-surface",
  info: "bg-[color:var(--info)] text-surface ring-surface",
}

export const FindingOverlay = React.memo(function Overlay({
  geom,
  findings,
  activeFindingId,
  onSelect,
  zoom = 1,
}: {
  geom: PageGeometry
  findings: Finding[]
  activeFindingId: FindingId | null
  onSelect: (id: FindingId) => void
  /**
   * The scale the page is drawn at. The overlay is scaled with the page, so
   * the box outlines and badges are counter-scaled by it to keep one
   * on-screen size at any zoom; only where they sit grows.
   */
  zoom?: number
}): JSX.Element {
  // Prefilter once per findings/page change. On a long label with many
  // findings, doing this inline in render walks the entire array on every
  // cursor tick and every hover.
  const boxes = React.useMemo(
    () => findings.filter((f) => f.placement === "box" && f.bbox?.page === geom.page),
    [findings, geom.page],
  )
  return (
    // `isolate` gives the overlay its own stacking context, so a box raised to
    // sit above its neighbours is raised only within the page. Without it those
    // local z-indexes compete with the rest of the application and a finding
    // box paints over a modal that is supposed to be covering the workspace.
    <div className="pointer-events-none absolute inset-0 isolate">
      {boxes.map((f) => {
        if (!f.bbox) return null
        const style = rectToStyle(f.bbox, geom)
        const active = activeFindingId === f.id
        return (
          <button
            key={f.id}
            type="button"
            data-finding-box={f.id}
            onClick={() => {
              onSelect(f.id)
            }}
            aria-label={`Finding ${String(f.number)}: ${f.claim}`}
            title={`Finding ${String(f.number)} · ${f.ruleId} — ${f.claim}`}
            className={cn(
              // Colours only: the border and halo widths change on every zoom
              // step, and animating them makes the box trail behind the page.
              "pointer-events-auto absolute rounded-[2px] border-2 transition-colors duration-150 ease-out",
              "hover:bg-opacity-30 hover:z-10",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[color:var(--brand)]",
              active ? BOX_TONE_ACTIVE[f.severity] : BOX_TONE[f.severity],
              active && "z-20 animate-[findingPulse_900ms_ease-out_1]",
            )}
            style={{
              ...style,
              borderWidth: 2 / zoom,
              ...(active ? { boxShadow: haloStyle(f.severity, zoom) } : {}),
            }}
          >
            {/* Badge in the left gutter, vertically centered. Label lines
                are ~14pt apart — a badge inside or above the box lands on
                the line before and obscures the very words it annotates. */}
            <span
              className={cn(
                "absolute -left-1 top-1/2 grid h-4 min-w-4 -translate-x-full -translate-y-1/2 place-items-center rounded-full px-1 font-id text-[10px] font-semibold leading-none tabular-nums ring-1 shadow-sm",
                active ? BADGE_TONE_ACTIVE[f.severity] : BADGE_IDLE,
              )}
              // Shrunk towards the box's edge, so it stays beside the box it numbers.
              style={{ scale: String(1 / zoom), transformOrigin: "right center" }}
              aria-hidden="true"
            >
              {f.number}
            </span>
          </button>
        )
      })}
    </div>
  )
})
